/**
 * GIWAHS — Notification Dispatcher (mode-switch)
 * ---------------------------------------------------------------------
 * Fire-and-forget transactional email dispatch. Two modes:
 *
 *   • Mock mode (default — when REACT_APP_NOTIFY_FN_URL is not set,
 *     or no Supabase URL is configured): writes a structured log line
 *     to the console — NO email is sent. Safe for dev + previews.
 *
 *   • Live mode: POSTs to a Supabase Edge Function (URL in env var) with
 *     the kind + payload. The Edge Function holds the RESEND_API_KEY and
 *     does the actual send. The frontend NEVER sees that key.
 *
 * Design rules:
 *   • track() is fired regardless of mode (email_dispatched/email_failed).
 *   • dispatch() never throws — failures are caught silently. Caller can
 *     await it, but a failed email must never break a buyer flow.
 *   • Retry: one inline retry on transient 5xx. No exponential backoff at
 *     the client; the Edge Function is expected to handle its own retries
 *     against Resend.
 *   • Payloads are passed through the existing PII filter on analytics
 *     events only — the actual email DOES need the buyer's address.
 *
 * To enable live mode:
 *   1. Deploy `/supabase/functions/send-notification` to your project.
 *   2. Set RESEND_API_KEY + EMAIL_FROM + EMAIL_ADMIN_NOTIFY_TO inside the
 *      Edge Function's environment (Supabase dashboard → Functions → env).
 *   3. Set REACT_APP_NOTIFY_FN_URL in the frontend .env to the function URL,
 *      e.g. `https://<project-ref>.supabase.co/functions/v1/send-notification`.
 *   4. Optionally set REACT_APP_NOTIFY_FN_KEY (defaults to the Supabase
 *      anon key from REACT_APP_SUPABASE_ANON_KEY).
 */
import { track, EVENTS } from './analytics';

const FN_URL  = process.env.REACT_APP_NOTIFY_FN_URL || '';
const FN_KEY  = process.env.REACT_APP_NOTIFY_FN_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const MODE    = FN_URL && FN_KEY ? 'live' : 'mock';

/**
 * Public entry point.
 * @param {string} kind  One of the keys exported by /lib/emails/templates.js.
 * @param {string} to    Primary recipient email. If null, only the admin
 *                       alert is dispatched (for kinds that have one).
 * @param {object} payload  Template payload — see templates.js for shape.
 */
export const dispatch = async ({ kind, to, payload = {}, meta = {} }) => {
  if (!kind) {
    console.warn('[GIWAHS notify] dispatch called without kind');
    return { ok: false, mode: MODE, error: 'missing_kind' };
  }

  // Analytics event — never contains the actual address (PII filter on
  // analytics strips it anyway, but we don't even attempt to pass it).
  track(EVENTS.NOTIFICATION_DISPATCHED, {
    kind,
    mode: MODE,
    has_recipient: !!to,
    ...meta,
  });

  if (MODE === 'mock') {
    // Dev/preview: log the intent — never actually send. Keep the log
    // line short so it doesn't dominate the console.
    // eslint-disable-next-line no-console
    console.info(`[GIWAHS notify · mock] kind=${kind} to=${to ? '<set>' : '<admin-only>'} `);
    // Fire the same lifecycle events in mock mode so funnel analytics
    // stay consistent across environments. The dashboard can filter by
    // the `mock` mode tag if needed.
    track(EVENTS.EMAIL_SENT,      { kind, mode: 'mock' });
    track(EVENTS.EMAIL_DELIVERED, { kind, mode: 'mock', provider: 'mock' });
    if (kind === 'rfq_confirmation') {
      track(EVENTS.RFQ_CONFIRMATION_SENT, { reference_code: payload?.reference_code, mode: 'mock' });
    }
    return { ok: true, mode: 'mock', skipped: true };
  }

  return _post({ kind, to, payload }, /* retryOn5xx */ true);
};

const _post = async (body, retryOn5xx) => {
  try {
    const res = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${FN_KEY}`,
        'apikey': FN_KEY,
      },
      body: JSON.stringify(body),
      // Use no-cache so a CDN never replays a stale 5xx.
      cache: 'no-store',
    });
    if (res.status >= 500 && retryOn5xx) {
      // One retry on transient server errors.
      await new Promise(r => setTimeout(r, 600));
      return _post(body, false);
    }
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error(`[GIWAHS notify] ${res.status}: ${errText.slice(0, 200)}`);
      track(EVENTS.NOTIFICATION_FAILED, { kind: body.kind, status: res.status });
      track(EVENTS.EMAIL_FAILED, { kind: body.kind, status: res.status });
      return { ok: false, mode: 'live', status: res.status, error: errText.slice(0, 200) };
    }
    const data = await res.json().catch(() => ({}));
    // Resend returns 200 + an id on accepted delivery. We surface
    // EMAIL_SENT on accept, EMAIL_DELIVERED is best-effort (Resend's
    // delivery webhook would be the real source, but at MVP we treat
    // 200 as delivered-to-provider — accurate semantics, not promised
    // inbox delivery).
    track(EVENTS.EMAIL_SENT, { kind: body.kind });
    track(EVENTS.EMAIL_DELIVERED, { kind: body.kind, provider: 'resend' });
    if (body.kind === 'rfq_confirmation') {
      track(EVENTS.RFQ_CONFIRMATION_SENT, { reference_code: body.payload?.reference_code });
    }
    return { ok: true, mode: 'live', ...data };
  } catch (err) {
    console.error('[GIWAHS notify] dispatch threw', err);
    track(EVENTS.NOTIFICATION_FAILED, { kind: body.kind, error_type: 'network' });
    track(EVENTS.EMAIL_FAILED, { kind: body.kind, error_type: 'network' });
    return { ok: false, mode: 'live', error: err?.message || 'network_error' };
  }
};

export const notifyMode = () => MODE;
